import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// --- Types ---

export interface ChartThemeContextValue {
  palette: string[];           // resolved hex strings — safe for Recharts fill/stroke
  paletteId: string;           // 'default' | 'warm' | 'cool' | 'mono' | 'custom'
  setPaletteId: (id: string) => void;
  setCustomPalette: (colors: string[]) => void;
}

// --- Preset palettes (palette data — hardcoded hex is intentional per CONTEXT.md) ---
// These represent designer-curated palette assets, not component styles.
// The prohibition on hardcoded hex in CLAUDE.md targets component colors
// (backgrounds, text, borders) — not palette data.

const PRESETS: Record<string, string[]> = {
  warm: ['#e63946', '#f4a261', '#e9c46a', '#f77f00', '#d62828', '#e07a5f'],
  cool: ['#457b9d', '#1d3557', '#a8dadc', '#06b6d4', '#0ea5e9', '#7c3aed'],
  mono: ['#1a1a2e', '#16213e', '#0f3460', '#533483', '#7b2d8b', '#9b5de5'],
};

// --- Default palette: read from CSS design tokens at init ---
// getComputedStyle resolves CSS vars to actual hex values.
// Recharts SVG engine does NOT resolve CSS variable strings in fill/stroke attributes.
// We resolve here once so palette contains hex strings throughout the app.

const DEFAULT_CSS_VARS = [
  '--color-accent',
  '--color-domain-benchmarking',
  '--color-domain-clustering',
  '--color-status-warning',
  '--color-domain-financial',
  '--color-status-error',
];

// Known fallback values in case CSS hasn't applied yet (SSR guard or JSDOM test env)
const DEFAULT_FALLBACK = ['#1976d2', '#7c3aed', '#059669', '#f57c00', '#2563eb', '#c62828'];

function readDefaultPalette(): string[] {
  if (typeof document === 'undefined') return DEFAULT_FALLBACK;
  const computed = getComputedStyle(document.documentElement);
  return DEFAULT_CSS_VARS.map((v, i) => {
    const resolved = computed.getPropertyValue(v).trim();
    return resolved || DEFAULT_FALLBACK[i];
  });
}

const defaultPalette = readDefaultPalette();

// --- Context ---

export const ChartThemeContext = createContext<ChartThemeContextValue>({
  palette: defaultPalette,
  paletteId: 'default',
  setPaletteId: () => {},
  setCustomPalette: () => {},
});

export const useChartTheme = (): ChartThemeContextValue => useContext(ChartThemeContext);

// --- Provider ---

export const ChartThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [paletteId, setPaletteId] = useState<string>('default');
  const [rawCustomPalette, setRawCustomPalette] = useState<string[]>([...defaultPalette]);

  let palette: string[];
  if (paletteId === 'custom') {
    palette = rawCustomPalette;
  } else if (paletteId in PRESETS) {
    palette = PRESETS[paletteId];
  } else {
    palette = defaultPalette;
  }

  // setCustomPalette also switches paletteId to 'custom' immediately so that
  // the custom colors are served right after the call without a separate
  // setPaletteId('custom') call.
  const setCustomPalette = useCallback((colors: string[]) => {
    setRawCustomPalette(colors);
    setPaletteId('custom');
  }, []);

  const contextValue = useMemo(
    () => ({ palette, paletteId, setPaletteId, setCustomPalette }),
    [palette, paletteId, setPaletteId, setCustomPalette],
  );

  return (
    <ChartThemeContext.Provider value={contextValue}>
      {children}
    </ChartThemeContext.Provider>
  );
};
